import { useEffect, useLayoutEffect, useRef } from 'react';
type SimpleSnowProps = {
  readonly flakeCount?: number;
  readonly radiusPx?: number;
  readonly blurPx?: number;
  readonly opacity?: number;
  spawnFromTop?: boolean;
  spawnRatePerSecond?: number;
  isActive?: boolean;
  fadeOutDurationMs?: number;
  onFadeComplete?: () => void;
  speedMultiplier?: number;
  coastDurationMs?: number;
  useCssFade?: boolean;
  cssFadeDurationMs?: number;
  zIndex?: number;
  opacityJitter?: number;
};
type Flake = {
  x: number;
  y: number;
  vy: number;
  drift: number;
  phase: number;
  opacityScale: number;
};
export const SimpleSnow = ({
  flakeCount = 80,
  radiusPx = 3,
  blurPx = 12,
  opacity = 0.93,
  spawnFromTop = false,
  spawnRatePerSecond = 160,
  isActive = true,
  fadeOutDurationMs = 1200,
  onFadeComplete,
  speedMultiplier = 1,
  coastDurationMs = 150,
  useCssFade = true,
  cssFadeDurationMs,
  zIndex = 1000,
  opacityJitter = 0.25
}: SimpleSnowProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prevActiveRef = useRef<boolean>(isActive);
  const activeRef = useRef<boolean>(isActive);
  const rafRef = useRef<number>(0);
  const cancelledRef = useRef<boolean>(false);
  const fadeCompletedRef = useRef<boolean>(false);
  useEffect(() => {
    activeRef.current = isActive;
    if (isActive) fadeCompletedRef.current = false;
  }, [isActive]);
  useLayoutEffect(() => {
    if (!useCssFade) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const duration = (cssFadeDurationMs ?? fadeOutDurationMs) || 0;
    canvas.style.transition = `opacity ${duration}ms ease-out`;
    canvas.style.opacity = isActive ? '1' : '0';
    let coastTimeoutId: number | undefined;
    let animationFrameIdStage1 = 0;
    let animationFrameIdStage2 = 0;
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'opacity') return;
      const style = getComputedStyle(canvas);
      if (style.opacity === '0' && !fadeCompletedRef.current) {
        fadeCompletedRef.current = true;
        onFadeComplete?.();
      }
    };
    canvas.addEventListener('transitionend', handleTransitionEnd);
    if (!isActive) {
      const delay = Math.max(0, coastDurationMs);
      coastTimeoutId = window.setTimeout(() => {
        animationFrameIdStage1 = requestAnimationFrame(() => {
          animationFrameIdStage2 = requestAnimationFrame(() => {
            canvas.style.opacity = '0';
          });
        });
      }, delay);
    } else {
      canvas.style.opacity = '1';
    }
    return () => {
      canvas.removeEventListener('transitionend', handleTransitionEnd);
      if (coastTimeoutId !== undefined) window.clearTimeout(coastTimeoutId);
      if (animationFrameIdStage1) cancelAnimationFrame(animationFrameIdStage1);
      if (animationFrameIdStage2) cancelAnimationFrame(animationFrameIdStage2);
    };
  }, [isActive, useCssFade, fadeOutDurationMs, coastDurationMs, onFadeComplete, cssFadeDurationMs]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    cancelledRef.current = false;
    let prevTime = 0;
    let fadeT = 0;
    let hasCompletedFade = false;
    const resize = () => {
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      const {
        innerWidth,
        innerHeight
      } = window;
      canvas.width = Math.max(1, Math.floor(innerWidth * dpr));
      canvas.height = Math.max(1, Math.floor(innerHeight * dpr));
      canvas.style.width = `${innerWidth}px`;
      canvas.style.height = `${innerHeight}px`;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const flakes: Flake[] = [];
    const seed = () => {
      flakes.length = 0;
      if (!spawnFromTop) {
        const {
          innerWidth,
          innerHeight
        } = window;
        for (let i = 0; i < flakeCount; i += 1) {
          const jitter = Math.max(0, Math.min(1, opacityJitter));
          flakes.push({
            x: Math.random() * innerWidth,
            y: Math.random() * innerHeight,
            vy: (0.35 + Math.random() * 0.9) * Math.max(0.01, speedMultiplier),
            drift: 0.2 + Math.random() * 0.6,
            phase: Math.random() * Math.PI * 2,
            opacityScale: 1 - Math.random() * jitter
          });
        }
      }
    };
    seed();
    const drawFlake = (x: number, y: number, alphaScale: number) => {
      const a0 = opacity * alphaScale;
      if (blurPx <= 0) {
        ctx.fillStyle = `rgba(255,255,255,${a0})`;
        ctx.beginPath();
        ctx.arc(x, y, radiusPx, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      const outer = radiusPx + blurPx;
      const grad = ctx.createRadialGradient(x, y, 0, x, y, outer);
      grad.addColorStop(0, `rgba(255,255,255,${a0})`);
      grad.addColorStop(Math.min(1, radiusPx / outer * 1), `rgba(255,255,255,${a0 * 0.7})`);
      grad.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, outer, 0, Math.PI * 2);
      ctx.fill();
    };
    const tick = (time: number) => {
      if (cancelledRef.current) return;
      const dt = prevTime ? Math.max(0, (time - prevTime) / 1000) : 0;
      prevTime = time;
      const {
        innerWidth,
        innerHeight
      } = window;
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      const currentActive = activeRef.current;
      const wasActive = prevActiveRef.current;
      prevActiveRef.current = currentActive;
      if (wasActive && !currentActive) {
        fadeT = 0;
      }
      if (!wasActive && currentActive && spawnFromTop) {
        flakes.length = 0;
      }
      const fading = !currentActive;
      if (fading) {
        fadeT += dt;
      }
      const coastS = Math.max(0, coastDurationMs / 1000);
      const fadeS = Math.max(0.001, fadeOutDurationMs / 1000);
      const timeIntoFade = Math.max(0, fadeT - coastS);
      const alphaScale = useCssFade ? 1 : fading ? Math.max(0, 1 - timeIntoFade / fadeS) : 1;
      if (!fading && spawnFromTop && flakes.length < flakeCount) {
        const spawnCountFloat = spawnRatePerSecond * dt;
        const toSpawn = Math.min(flakeCount - flakes.length, Math.max(0, Math.floor(spawnCountFloat)) || (dt > 0 ? 1 : 0));
        for (let i = 0; i < toSpawn; i += 1) {
          const jitter = Math.max(0, Math.min(1, opacityJitter));
          flakes.push({
            x: Math.random() * innerWidth,
            y: -radiusPx - Math.random() * (radiusPx * 4),
            vy: (0.35 + Math.random() * 0.9) * Math.max(0.01, speedMultiplier),
            drift: 0.2 + Math.random() * 0.6,
            phase: Math.random() * Math.PI * 2,
            opacityScale: 1 - Math.random() * jitter
          });
        }
      }
      const alive: Flake[] = [];
      for (const f of flakes) {
        f.y += f.vy;
        f.x += Math.sin(f.phase) * f.drift * 0.6;
        f.phase += 0.02;
        if (f.y - radiusPx > innerHeight) {
          if (!fading) {
            f.y = -radiusPx;
            f.x = Math.random() * innerWidth;
          } else {
            continue;
          }
        }
        if (f.x < -radiusPx) f.x = innerWidth + radiusPx;
        if (f.x > innerWidth + radiusPx) f.x = -radiusPx;
        drawFlake(f.x, f.y, alphaScale * f.opacityScale);
        alive.push(f);
      }
      flakes.length = 0;
      flakes.push(...alive);
      if (!useCssFade && fading && alphaScale <= 0 && flakes.length === 0) {
        if (!hasCompletedFade) {
          hasCompletedFade = true;
          if (onFadeComplete) onFadeComplete();
        }
        prevTime = time;
        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    window.addEventListener('resize', resize);
    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      cancelledRef.current = true;
      window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [flakeCount, radiusPx, blurPx, opacity, spawnFromTop, spawnRatePerSecond, fadeOutDurationMs, onFadeComplete, speedMultiplier, coastDurationMs, useCssFade, opacityJitter]);
  return <canvas ref={canvasRef} style={{
    position: 'fixed',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex
  }} data-sentry-component="SimpleSnow" data-sentry-source-file="simple-snow.tsx" />;
};